/**
 * Painel de Filtros Avançados de Busca
 */

import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Button,
  Divider,
  Collapse,
  IconButton,
  Slider,
  alpha
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import PersonIcon from '@mui/icons-material/Person'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import FolderIcon from '@mui/icons-material/Folder'
import CategoryIcon from '@mui/icons-material/Category'
import {
  SearchFilters as SearchFiltersType,
  SearchScope,
  DEFAULT_SEARCH_FILTERS,
  DEFAULT_SEARCH_SCOPE
} from '../types/search.types'
import { EntityType, SentimentLabel } from '../../../types/transcription'
import type { TranscriptionFolder } from '../../folders/types/folder.types'

interface SearchFiltersProps {
  /** Filtros atuais */
  filters: SearchFiltersType
  /** Escopo atual */
  scope: SearchScope
  /** Callback para atualizar filtros */
  onFiltersChange: (filters: Partial<SearchFiltersType>) => void
  /** Callback para atualizar escopo */
  onScopeChange: (scope: Partial<SearchScope>) => void
  /** Falantes disponíveis */
  availableSpeakers?: string[]
  /** Pastas disponíveis */
  availableFolders?: TranscriptionFolder[]
  /** Modo compacto (inline) */
  compact?: boolean
  /** Callback para aplicar filtros */
  onApply?: () => void
}

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  'PERSON': 'Pessoas',
  'ORGANIZATION': 'Organizações',
  'LOCATION': 'Locais',
  'DATE': 'Datas',
  'TIME': 'Horários',
  'MONEY': 'Valores',
  'LEGAL_TERM': 'Termos Jurídicos',
  'EVIDENCE': 'Evidências'
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Positivo',
    icon: <SentimentSatisfiedAltIcon sx={{ color: '#4caf50' }} />,
    color: '#4caf50'
  },
  neutral: {
    label: 'Neutro',
    icon: <SentimentNeutralIcon sx={{ color: '#9e9e9e' }} />,
    color: '#9e9e9e'
  },
  negative: {
    label: 'Negativo',
    icon: <SentimentDissatisfiedIcon sx={{ color: '#f44336' }} />,
    color: '#f44336'
  }
}

export default function SearchFilters({
  filters,
  scope,
  onFiltersChange,
  onScopeChange,
  availableSpeakers = [],
  availableFolders = [],
  compact = false,
  onApply
}: SearchFiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    scope: true,
    speakers: true,
    sentiment: false,
    entities: false,
    advanced: false
  })

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handleClearAll = () => {
    onFiltersChange(DEFAULT_SEARCH_FILTERS)
    onScopeChange(DEFAULT_SEARCH_SCOPE)
  }

  const hasActiveFilters = () => {
    return (
      (filters.speakers?.length || 0) > 0 ||
      (filters.sentiment?.length || 0) > 0 ||
      (filters.entityTypes?.length || 0) > 0 ||
      filters.minConfidence !== undefined ||
      filters.hasAnnotations ||
      filters.legalTermsOnly
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // SECTION HEADER
  // ═══════════════════════════════════════════════════════════════

  const SectionHeader = ({
    title,
    section,
    icon,
    count
  }: {
    title: string
    section: string
    icon: React.ReactNode
    count?: number
  }) => (
    <Box
      onClick={() => toggleSection(section)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { bgcolor: alpha('#000', 0.04) }
      }}
    >
      {icon}
      <Typography variant="subtitle2" sx={{ flex: 1 }}>
        {title}
      </Typography>
      {count !== undefined && count > 0 && (
        <Chip label={count} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
      )}
      {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </Box>
  )

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <Paper
      elevation={compact ? 0 : 1}
      sx={{
        p: compact ? 1 : 2,
        borderRadius: 2,
        border: compact ? 'none' : '1px solid #e0e0e0',
        // ── WordPress CSS Isolation Overrides ──────────────────────
        '#monkee-file-transcript-client & .MuiTypography-subtitle1': {
          fontSize: '1rem !important',
          fontWeight: '600 !important',
          lineHeight: '1.5 !important',
        },
        '#monkee-file-transcript-client & .MuiTypography-subtitle2': {
          fontSize: '0.875rem !important',
          fontWeight: '600 !important',
          lineHeight: '1.57 !important',
        },
        '#monkee-file-transcript-client & .MuiTypography-body2': {
          fontSize: '0.875rem !important',
          fontWeight: '400 !important',
          lineHeight: '1.43 !important',
        },
        '#monkee-file-transcript-client & .MuiTypography-caption': {
          fontSize: '0.75rem !important',
          fontWeight: '400 !important',
          lineHeight: '1.66 !important',
        },
        '#monkee-file-transcript-client & hr.MuiDivider-root, #monkee-file-transcript-client & .MuiDivider-root': {
          borderTopColor: 'rgba(0,0,0,0.12) !important',
          borderTopStyle: 'solid !important',
          borderTopWidth: '1px !important',
          borderBottom: 'none !important',
          borderLeft: 'none !important',
          borderRight: 'none !important',
          borderImage: 'none !important',
          marginTop: '8px !important',
          marginBottom: '8px !important',
          marginLeft: '0 !important',
          marginRight: '0 !important',
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          height: '0 !important',
          display: 'block !important',
        },
      }}
    >
      {/* Header */}
      {!compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filtros Avançados
            </Typography>
          </Box>
          {hasActiveFilters() && (
            <Button
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAll}
              sx={{ textTransform: 'none' }}
            >
              Limpar
            </Button>
          )}
        </Box>
      )}

      {/* Limpar filtros (compact mode) */}
      {compact && hasActiveFilters() && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={handleClearAll}
            sx={{ textTransform: 'none' }}
          >
            Limpar filtros
          </Button>
        </Box>
      )}

      {/* Escopo de Busca */}
      <SectionHeader
        title="Buscar em"
        section="scope"
        icon={<CategoryIcon fontSize="small" color="action" />}
      />
      <Collapse in={expandedSections.scope}>
        <Box sx={{ pl: 4, pr: 1, pt: 1.5, pb: 2 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={scope.transcriptions}
                  onChange={(e) => onScopeChange({ transcriptions: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Transcrições</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={scope.entities}
                  onChange={(e) => onScopeChange({ entities: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Entidades (pessoas, locais...)</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={scope.summaries}
                  onChange={(e) => onScopeChange({ summaries: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Resumos de IA</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={scope.annotations}
                  onChange={(e) => onScopeChange({ annotations: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Anotações</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={scope.filenames}
                  onChange={(e) => onScopeChange({ filenames: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Nomes de arquivos</Typography>}
            />
          </FormGroup>
        </Box>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Falantes */}
      {availableSpeakers.length > 0 && (
        <>
          <SectionHeader
            title="Falantes"
            section="speakers"
            icon={<PersonIcon fontSize="small" color="action" />}
            count={filters.speakers?.length}
          />
          <Collapse in={expandedSections.speakers}>
            <Box sx={{ pl: 4, pr: 1, pt: 1.5, pb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {availableSpeakers.map(speaker => {
                  const isSelected = filters.speakers?.includes(speaker)
                  return (
                    <Chip
                      key={speaker}
                      label={speaker}
                      size="small"
                      variant={isSelected ? 'filled' : 'outlined'}
                      color={isSelected ? 'primary' : 'default'}
                      onClick={() => {
                        const current = filters.speakers || []
                        const newSpeakers = isSelected
                          ? current.filter(s => s !== speaker)
                          : [...current, speaker]
                        onFiltersChange({ speakers: newSpeakers.length > 0 ? newSpeakers : undefined })
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  )
                })}
              </Box>
            </Box>
          </Collapse>
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* Sentimento */}
      <SectionHeader
        title="Sentimento"
        section="sentiment"
        icon={<SentimentNeutralIcon fontSize="small" color="action" />}
        count={filters.sentiment?.length}
      />
      <Collapse in={expandedSections.sentiment}>
        <Box sx={{ pl: 4, pr: 1, pt: 1.5, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(Object.entries(SENTIMENT_CONFIG) as [SentimentLabel, typeof SENTIMENT_CONFIG.positive][]).map(
              ([sentiment, config]) => {
                const isSelected = filters.sentiment?.includes(sentiment)
                return (
                  <Chip
                    key={sentiment}
                    icon={config.icon}
                    label={config.label}
                    size="small"
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => {
                      const current = filters.sentiment || []
                      const newSentiment = isSelected
                        ? current.filter(s => s !== sentiment)
                        : [...current, sentiment]
                      onFiltersChange({
                        sentiment: newSentiment.length > 0 ? newSentiment : undefined
                      })
                    }}
                    sx={{
                      cursor: 'pointer',
                      borderColor: isSelected ? config.color : undefined,
                      bgcolor: isSelected ? alpha(config.color, 0.1) : undefined
                    }}
                  />
                )
              }
            )}
          </Box>
        </Box>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Tipos de Entidade */}
      <SectionHeader
        title="Tipos de Entidade"
        section="entities"
        icon={<CategoryIcon fontSize="small" color="action" />}
        count={filters.entityTypes?.length}
      />
      <Collapse in={expandedSections.entities}>
        <Box sx={{ pl: 4, pr: 1, pt: 1.5, pb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(Object.entries(ENTITY_TYPE_LABELS) as [EntityType, string][]).map(
              ([type, label]) => {
                const isSelected = filters.entityTypes?.includes(type)
                return (
                  <Chip
                    key={type}
                    label={label}
                    size="small"
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    onClick={() => {
                      const current = filters.entityTypes || []
                      const newTypes = isSelected
                        ? current.filter(t => t !== type)
                        : [...current, type]
                      onFiltersChange({
                        entityTypes: newTypes.length > 0 ? newTypes : undefined
                      })
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                )
              }
            )}
          </Box>
        </Box>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Opções Avançadas */}
      <SectionHeader
        title="Opções Avançadas"
        section="advanced"
        icon={<FilterListIcon fontSize="small" color="action" />}
      />
      <Collapse in={expandedSections.advanced}>
        <Box sx={{ pl: 4, pr: 1, pt: 1.5, pb: 2 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hasAnnotations || false}
                  onChange={(e) => onFiltersChange({
                    hasAnnotations: e.target.checked || undefined
                  })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Apenas com anotações</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.legalTermsOnly || false}
                  onChange={(e) => onFiltersChange({
                    legalTermsOnly: e.target.checked || undefined
                  })}
                  size="small"
                />
              }
              label={<Typography variant="body2">Apenas termos jurídicos</Typography>}
            />
          </FormGroup>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Confiança mínima da transcrição
            </Typography>
            <Slider
              value={(filters.minConfidence || 0) * 100}
              onChange={(_, value) => onFiltersChange({
                minConfidence: (value as number) / 100 || undefined
              })}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              step={10}
              marks
              min={0}
              max={100}
              size="small"
            />
          </Box>
        </Box>
      </Collapse>

      {/* Botão Aplicar (se houver callback) */}
      {onApply && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onApply}
            sx={{ borderRadius: 2 }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      )}
    </Paper>
  )
}
